import { useEffect, useMemo, useState } from 'react';
import type { EnvLinkRow } from '../../lib/envLinksApi';

const EXAMPLES = [
  'Click the Spin button',
  'Open the paytable / info menu',
  'Close any popup or dialog',
  'Find the max-win or RTP text',
  'Open settings and capture responsible-gaming text',
];

const CUSTOM = '__custom__';

function parseUrlParams(url: string): Record<string, string> {
  try {
    const u = new URL(url);
    const out: Record<string, string> = {};
    u.searchParams.forEach((v, k) => {
      out[k.toLowerCase()] = v;
    });
    return out;
  } catch {
    return {};
  }
}

function rowUrl(row: EnvLinkRow): string {
  return (row.generatedUrl || row.playUrl || '').trim();
}

function rowLabel(row: EnvLinkRow): string {
  const params = parseUrlParams(rowUrl(row));
  const bits = [row.jurisdiction];
  if (params.currency) bits.push(params.currency);
  const lang = params.language ?? params.lang;
  if (lang) bits.push(lang);
  if (row.status && row.status !== 'ok') bits.push(row.status);
  return bits.join(' · ');
}

interface UrlOption {
  id: string;
  label: string;
  url: string;
}

function optionsFromRows(rows: EnvLinkRow[]): UrlOption[] {
  const ready: UrlOption[] = [];
  const rest: UrlOption[] = [];
  rows.forEach((row, i) => {
    const url = rowUrl(row);
    if (!url) return;
    const opt: UrlOption = { id: `env-${i}`, label: rowLabel(row), url };
    if (row.status === 'ok') ready.push(opt);
    else rest.push(opt);
  });
  return [...ready, ...rest];
}

interface Props {
  envLinkRows: EnvLinkRow[] | null;
  recipeUser: string | null;
  hasRecipePassword: boolean;
  engineOk: boolean;
  engineError?: string;
  running: boolean;
  executing: boolean;
  awaitingCredentials: boolean;
  error: string | null;
  adapters: { id: string; label: string; model: string; configured: boolean }[];
  adapter: string;
  onAdapterChange: (id: string) => void;
  onStart: (url: string, task?: string, adapter?: string) => void;
  onExecute: (task: string) => void;
  onExplore: (prompt: string) => void;
  onStop: () => void;
  onRefresh: () => void;
  onSubmitCreds: (user: string, password: string, extra?: string) => void;
}

export function LookAtThatControls(props: Props) {
  const [gameUrl, setGameUrl] = useState('');
  const [urlSource, setUrlSource] = useState(CUSTOM);
  const [seeded, setSeeded] = useState(false);
  const [task, setTask] = useState('');
  const [user, setUser] = useState(props.recipeUser ?? '');
  const [password, setPassword] = useState('');
  const [extra, setExtra] = useState('');

  const urlOptions = useMemo(
    () => optionsFromRows(props.envLinkRows ?? []),
    [props.envLinkRows],
  );

  useEffect(() => {
    if (seeded || props.running) return;
    if (gameUrl.trim()) {
      setSeeded(true);
      return;
    }
    if (props.envLinkRows === null) return;
    const first = urlOptions[0];
    if (first) {
      setUrlSource(first.id);
      setGameUrl(first.url);
    } else {
      setUrlSource(CUSTOM);
    }
    setSeeded(true);
  }, [seeded, props.running, props.envLinkRows, urlOptions, gameUrl]);

  useEffect(() => {
    if (props.recipeUser && !user) setUser(props.recipeUser);
  }, [props.recipeUser, user]);

  const pickSource = (id: string) => {
    setUrlSource(id);
    if (id === CUSTOM) return;
    const opt = urlOptions.find((o) => o.id === id);
    if (opt) setGameUrl(opt.url);
  };

  const onUrlTyped = (value: string) => {
    setGameUrl(value);
    if (!value.trim()) setUrlSource(CUSTOM);
  };

  return (
    <div className="card flex-col" style={{ gap: 10 }}>
      <h3 style={{ margin: 0 }}>Controls</h3>
      {!props.engineOk && (
        <p className="small" style={{ color: 'var(--bad)', margin: 0 }}>
          Engine offline{props.engineError ? `: ${props.engineError}` : '. Install Python + Playwright (see deploy docs).'}
        </p>
      )}
      {props.hasRecipePassword && (
        <p className="muted small" style={{ margin: 0 }}>
          Studio recipe has tester credentials for {props.recipeUser || 'this studio'} — they are tried first on login pages.
        </p>
      )}
      <label className="muted small">Computer-use adapter</label>
      <select
        value={props.adapter}
        onChange={(e) => props.onAdapterChange(e.target.value)}
        disabled={props.running}
      >
        {(props.adapters.length
          ? props.adapters
          : [
              { id: 'openai_ga', label: 'OpenAI (gpt-5.4-mini)', model: 'gpt-5.4-mini', configured: true },
              { id: 'anthropic', label: 'Claude Sonnet 5', model: 'claude-sonnet-5', configured: true },
              { id: 'gemini', label: 'Gemini 3.8 Flash', model: 'gemini-3.8-flash', configured: true },
            ]
        ).map((a) => (
          <option key={a.id} value={a.id} disabled={!a.configured}>
            {a.label}
            {a.configured ? '' : ' (no API key)'}
          </option>
        ))}
      </select>
      <p className="muted small" style={{ margin: 0 }}>
        Computer-use clicks and live-play PASS/FAIL use this adapter. Ask-the-bot, calibrate
        inventory, and memory still use OpenAI vision. Switch between sessions to A/B.
      </p>
      {props.error && (
        <p className="small" style={{ color: 'var(--bad)', margin: 0 }}>
          {props.error}
        </p>
      )}

      <label className="muted small">Game URL</label>
      <p className="muted small" style={{ margin: 0 }}>
        Pick a Phase 2 market link, or Custom URL to paste your own. Clearing the
        box does not restore a market.
      </p>
      <select
        value={urlOptions.some((o) => o.id === urlSource) ? urlSource : CUSTOM}
        onChange={(e) => pickSource(e.target.value)}
        disabled={props.running}
      >
        {urlOptions.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        <option value={CUSTOM}>Custom URL</option>
      </select>
      <input
        value={gameUrl}
        onChange={(e) => onUrlTyped(e.target.value)}
        placeholder="https://…"
        disabled={props.running}
      />

      <label className="muted small">Task / explore prompt</label>
      <textarea
        value={task}
        onChange={(e) => setTask(e.target.value)}
        rows={4}
        placeholder="e.g. Open the paytable and show the max-win rule"
      />
      <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
        {EXAMPLES.map((ex) => (
          <button key={ex} type="button" className="btn small btn-ghost" onClick={() => setTask(ex)}>
            {ex}
          </button>
        ))}
      </div>

      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        {!props.running ? (
          <button
            type="button"
            className="btn btn-success"
            disabled={!props.engineOk || !gameUrl.trim()}
            onClick={() => props.onStart(gameUrl.trim(), task.trim() || undefined, props.adapter)}
          >
            Start session
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!task.trim() || props.executing}
              onClick={() => props.onExecute(task.trim())}
            >
              {props.executing ? 'Working…' : 'Execute'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!task.trim() || props.executing}
              onClick={() => props.onExplore(task.trim())}
            >
              Explore
            </button>
            <button type="button" className="btn" disabled={props.executing} onClick={props.onRefresh}>
              Refresh
            </button>
            <button type="button" className="btn btn-danger" onClick={props.onStop}>
              Stop
            </button>
          </>
        )}
      </div>

      {props.awaitingCredentials && (
        <div className="card" style={{ borderColor: 'var(--warn)', margin: 0 }}>
          <h3 style={{ margin: '0 0 8px' }}>Credentials needed</h3>
          <p className="muted small" style={{ marginTop: 0 }}>
            The bot hit a login or validation page. Enter tester credentials — they are typed into the live
            session and are not stored.
          </p>
          <label className="muted small">User</label>
          <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="username" />
          <label className="muted small">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <label className="muted small">Extra (token / age, optional)</label>
          <input value={extra} onChange={(e) => setExtra(e.target.value)} />
          <button
            type="button"
            className="btn btn-success"
            style={{ marginTop: 8 }}
            disabled={!user.trim() || !password}
            onClick={() => props.onSubmitCreds(user.trim(), password, extra.trim() || undefined)}
          >
            Type credentials and continue
          </button>
        </div>
      )}
    </div>
  );
}
